import type {
	IJob,
	IJobContract,
	IJobDocuments,
	IJobInvoice,
	IJobLabor,
	IJobMaterial,
	IJobOpenAR,
	IJobOther,
	IJobPayments,
	IJobProbability,
	IJobRole,
	IJobStatus,
	IJobSubContract,
	IJobTimeline,
	IJobTotal,
	IJobType
} from '@/interfaces'
import { Job, JobIndex } from '@/models'
import { BaseService, type IAuthService } from '@/services'
import { APIEndpoints, APIScopes, APIURL } from '@/services/API'
export interface IJobID {
	JobID: string
}
export class JobService extends BaseService {
	constructor(authService: IAuthService) {
		super(APIURL, authService, APIScopes)
	}
	async getIndex(params?: object): Promise<JobIndex[]> {
		return this.getData<JobIndex[]>(`${APIEndpoints.job}/`, params)
			.then((data) => {
				// Handle "No results Found" response from API
				if (data && typeof data === 'object' && !Array.isArray(data) && 'Message' in data) {
					return []
				}
				return data.map((c) => new JobIndex(c))
			})
			.catch((err) => {
				throw err
			})
	}
	async get(id: string): Promise<Job> {
		return this.getData<Job>(`${APIEndpoints.job}/${id}`)
			.then((data) => {
				return new Job(data)
			})
			.catch((err) => {
				throw err
			})
	}
	async add(job: Job): Promise<IJob> {
		const obj = job
		return this.postData<Job, IJob>(`${APIEndpoints.job}/add`, obj)
			.then((res) => {
				return res as IJob
			})
			.catch((err) => {
				throw err
			})
	}
	async update(job: Job): Promise<Job> {
		const obj = job
		console.log(obj)
		return this.patchData(`${APIEndpoints.job}/update`, obj)
			.then((data) => {
				return new Job(data)
			})
			.catch((err) => {
				throw err
			})
	}
	// TODO - move to subjob service and store when we build it out
	async getRoles(): Promise<IJobRole[]> {
		return this.getData<IJobRole[]>(`${APIEndpoints.subJob}/roles`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}
	async getTimelines(): Promise<IJobTimeline[]> {
		return this.getData<IJobTimeline[]>(`${APIEndpoints.job}/timeline`)
			.then((data) => {
				return data as IJobTimeline[]
			})
			.catch((err) => {
				throw err
			})
	}
	async getScopes(): Promise<string[]> {
		return this.getData<string[]>(`${APIEndpoints.job}/scopes`)
			.then((data) => {
				return data as string[]
			})
			.catch((err) => {
				throw err
			})
	}
	async getProbabilities(): Promise<IJobProbability[]> {
		return this.getData<IJobProbability[]>(`${APIEndpoints.job}/probability`)
			.then((data) => {
				return data as IJobProbability[]
			})
			.catch((err) => {
				throw err
			})
	}
	async getStatuses(): Promise<IJobStatus[]> {
		return this.getData<IJobStatus[]>(`${APIEndpoints.job}/statuses`)
			.then((data) => {
				return data as IJobStatus[]
			})
			.catch((err) => {
				throw err
			})
	}
	async getTypes(): Promise<IJobType[]> {
		return this.getData<IJobType[]>(`${APIEndpoints.job}/types`)
			.then((data) => {
				return data as IJobType[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobContract(id: string): Promise<IJobContract[]> {
		return this.getData<IJobContract[]>(`${APIEndpoints.job}/summary/${id}/contract`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobInvoice(id: string): Promise<IJobInvoice[]> {
		return this.getData<IJobInvoice[]>(`${APIEndpoints.job}/summary/${id}/invoice`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobOpenAR(id: string): Promise<IJobOpenAR[]> {
		return this.getData<IJobOpenAR[]>(`${APIEndpoints.job}/summary/${id}/open-ar`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobPayments(id: string): Promise<IJobPayments[]> {
		return this.getData<IJobPayments[]>(`${APIEndpoints.job}/summary/${id}/payments`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobLabor(id: string): Promise<IJobLabor[]> {
		return this.getData<IJobLabor[]>(`${APIEndpoints.job}/summary/${id}/labor`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobLaborDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.job}/summary/${id}/labor/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobMaterial(id: string): Promise<IJobMaterial[]> {
		return this.getData<IJobMaterial[]>(`${APIEndpoints.job}/summary/${id}/materials`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobMaterialDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.job}/summary/${id}/material/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobOther(id: string): Promise<IJobOther[]> {
		return this.getData<IJobOther[]>(`${APIEndpoints.job}/summary/${id}/other`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobOtherDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.job}/summary/${id}/other/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobSubContract(id: string): Promise<IJobSubContract[]> {
		return this.getData<IJobSubContract[]>(`${APIEndpoints.job}/summary/${id}/subcontract`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobSubContractDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.job}/summary/${id}/subcontract/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobTotal(id: string): Promise<IJobTotal[]> {
		return this.getData<IJobTotal[]>(`${APIEndpoints.job}/summary/${id}/total`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobTotalDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.job}/summary/${id}/total/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getJobDocuments(id: string): Promise<IJobDocuments[]> {
		return this.getData<IJobDocuments[]>(`${APIEndpoints.job}/${id}/documents`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}
}
